import { useEffect, useState } from 'preact/hooks';
import {
  fetchOffers, acceptOffer, rejectOffer,
  getMySwapOffers, decideSwapOffer,
  type Offer, type SwapOffer, CACHE_KEYS,
} from '../lib/api';
import { getCached } from '../lib/cache';
import { Skeleton } from '../components/Skeleton';
import { haptic } from '../lib/telegram';

const MONTH_ES_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DAY_ES_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

function fmtFullDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso || '';
  // Treat as AST noon to avoid TZ shifts on the day-of-week lookup.
  const dt = new Date(`${iso}T12:00:00-04:00`);
  if (isNaN(dt.getTime())) return iso;
  const day = DAY_ES_SHORT[dt.getUTCDay()] || '';
  const dnum = parseInt(m[3], 10);
  const mon = MONTH_ES_SHORT[parseInt(m[2], 10) - 1] || '';
  return `${day} ${dnum} ${mon}`;
}

function fmtExpiry(iso: string): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return '';
  const ms = dt.getTime() - Date.now();
  if (ms <= 0) return 'vencida';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `vence en ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vence en ${hours} h`;
  const days = Math.round(hours / 24);
  return `vence en ${days} d`;
}

export function Ofertas() {
  const [data, setData] = useState<{ offer: Offer | null } | null>(null);
  const [swapOffers, setSwapOffers] = useState<SwapOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<'accept' | 'reject' | null>(null);
  // Per-swap-offer submit state, keyed by reqId. Two different sources of busy:
  // the single replacement-offer above, and any number of swap offers below.
  const [swapBusy, setSwapBusy] = useState<Record<string, 'accept' | 'reject' | null>>({});

  useEffect(() => { load(); }, []);

  async function load() {
    setError(null);
    const cached = getCached<{ offer: Offer | null }>(CACHE_KEYS.offers);
    if (cached) {
      setData(cached.value);
      setLoading(false);
    } else {
      setLoading(true);
    }
    // Replacement offer + swap offers fan out in parallel — independent paths.
    const [offersRes, swapsRes] = await Promise.allSettled([
      fetchOffers(),
      getMySwapOffers(),
    ]);
    if (offersRes.status === 'fulfilled') {
      setData(offersRes.value);
    } else if (!cached) {
      setError((offersRes.reason as Error).message || 'No pudimos cargar tus ofertas');
    }
    if (swapsRes.status === 'fulfilled') {
      setSwapOffers(swapsRes.value.offers || []);
    }
    setLoading(false);
  }

  async function handleSwapDecision(reqId: string, decision: 'accept' | 'reject') {
    if (swapBusy[reqId]) return;
    const offer = swapOffers.find((o) => o.reqId === reqId);
    if (!offer) return;
    const verb = decision === 'accept' ? 'aceptar cubrir' : 'rechazar';
    if (!confirm(`¿Seguro que quieres ${verb} el turno de ${offer.fromName} en ${offer.eventoId}?`)) return;

    setSwapBusy((s) => ({ ...s, [reqId]: decision }));
    haptic('medium');
    try {
      const r = await decideSwapOffer(reqId, decision);
      haptic('success');
      // Remove the offer immediately (server already updated state, no need to re-fetch).
      setSwapOffers((arr) => arr.filter((o) => o.reqId !== reqId));
      alert(decision === 'accept'
        ? (r.status === 'ops_pending'
          // Since 2026-07-20 every app swap holds for ops approval after the
          // target accepts; 'accepted' only appears if an old bot executes directly.
          ? `✓ Aceptaste cubrir ${offer.eventoId}. Falta la aprobación de Operaciones — te avisamos por Telegram.`
          : `✓ Confirmado. Ahora estás asignado a ${offer.eventoId} como ${offer.rol}.`)
        : '✓ Rechazado. Le avisamos a quien lo pidió.');
    } catch (e) {
      const msg = (e as Error).message;
      haptic('error');
      if (msg === 'request_not_found' || msg === 'request_not_pending' || msg === 'request_expired') {
        alert('Esta solicitud ya no está activa.');
        await load();
      } else if (msg === 'event_started') {
        alert('Ese evento ya empezó — el cambio de turno ya no se puede hacer.');
        await load();
      } else if (msg === 'event_not_found') {
        alert('No encontramos ese evento — avisa a Operaciones.');
        await load();
      } else if (msg === 'event_postponed') {
        alert('Ese evento está pospuesto — el cambio de turno ya no aplica.');
        await load();
      } else if (msg === 'event_closed') {
        alert('Ese evento ya está en cierre o cerrado — el cambio de turno ya no aplica.');
        await load();
      } else if (msg === 'target_no_longer_eligible') {
        alert('Ya no eres elegible para este cambio (puede que estés asignado a otro evento ese día).');
        await load();
      } else {
        alert(`No pudimos procesar tu respuesta: ${msg}`);
      }
    } finally {
      setSwapBusy((s) => { const next = { ...s }; delete next[reqId]; return next; });
    }
  }

  async function handleDecision(decision: 'accept' | 'reject') {
    if (submitting) return;
    const offer = data?.offer;
    if (!offer) return;
    const verb = decision === 'accept' ? 'aceptar' : 'rechazar';
    if (!confirm(`¿Seguro que quieres ${verb} esta oferta para ${offer.eventoId}?`)) return;

    setSubmitting(decision);
    haptic('medium');
    try {
      if (decision === 'accept') await acceptOffer();
      else await rejectOffer();
      haptic('success');
      await load();
      alert(decision === 'accept'
        ? '✓ Oferta aceptada. Te llegará la confirmación con los detalles del evento.'
        : '✓ Oferta rechazada. Sophi seguirá buscando.');
    } catch (e) {
      const msg = (e as Error).message;
      haptic('error');
      if (msg === 'no_offer') {
        alert('Esta oferta ya no está disponible. Refrescamos la pantalla.');
        await load();
      } else if (msg === 'no_chat_id') {
        alert('No tienes un Telegram chat ID registrado. Habla con Angie.');
      } else if (msg === 'bridge_unavailable') {
        alert('No pudimos contactar a Sophi. Intenta de nuevo en un momento.');
      } else {
        alert(`No pudimos procesar tu respuesta: ${msg}`);
      }
    } finally {
      setSubmitting(null);
    }
  }

  if (loading) {
    return (
      <div class="screen">
        <header class="app-header"><h1>Ofertas</h1></header>
        <div class="card" style={{ padding: 16 }}>
          <Skeleton width="70%" height={17} style={{ marginBottom: 8 }} />
          <Skeleton width="50%" height={13} style={{ marginBottom: 6 }} />
          <Skeleton width="60%" height={13} style={{ marginBottom: 16 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Skeleton height={40} borderRadius={10} />
            <Skeleton height={40} borderRadius={10} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-screen">
        <h2>No pudimos cargar</h2>
        <p>{error}</p>
        <button class="btn primary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  const offer = data?.offer || null;
  const noOffers = !offer && swapOffers.length === 0;

  return (
    <div class="screen">
      <header class="app-header">
        <h1>Ofertas</h1>
      </header>

      {noOffers ? (
        <div class="empty">
          <div class="ic">🎁</div>
          <p>No tienes ofertas pendientes ahora.</p>
          <p class="hint">
            Cuando alguien cancela un evento y Sophi te elige como reemplazo —
            o cuando un compañero te pide cubrirle un turno — aparece aquí.
          </p>
        </div>
      ) : null}

      {/* Swap offers (from teammates) — render first so they're not buried below
          a long replacement-offer card. Each one is independent state. */}
      {swapOffers.length > 0 && (
        <>
          <div class="section-header">Cambios de turno</div>
          {swapOffers.map((s) => {
            const busy = swapBusy[s.reqId];
            return (
              <div key={s.reqId} class="card swap-offer-card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div class="lt-title" style={{ fontSize: '1.05rem' }}>
                    🔄 <strong>{s.fromName || 'Un compañero'}</strong> te pide cubrir
                  </div>
                  <div class="lt-sub">
                    {s.rol}
                    {s.eventoFecha && ` · ${fmtFullDate(s.eventoFecha)}`}
                    {` · ${s.eventoId}`}
                  </div>
                  {s.expiresAt && (
                    <div class="lt-sub" style={{ marginTop: 6, color: 'var(--color-warning)' }}>
                      ⏳ {fmtExpiry(s.expiresAt)}
                    </div>
                  )}
                  <div class="lt-sub" style={{ marginTop: 6 }}>
                    Si aceptas, Operaciones da la aprobación final. <strong>Sin penalidad para nadie.</strong>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 16 }}>
                  <button
                    class="btn primary"
                    disabled={busy !== undefined && busy !== null}
                    onClick={() => handleSwapDecision(s.reqId, 'accept')}
                  >
                    {busy === 'accept' ? 'Enviando…' : 'Aceptar'}
                  </button>
                  <button
                    class="btn"
                    disabled={busy !== undefined && busy !== null}
                    onClick={() => handleSwapDecision(s.reqId, 'reject')}
                  >
                    {busy === 'reject' ? 'Enviando…' : 'Rechazar'}
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Replacement offers (from Sophi after someone cancelled) — single offer per
          staff at a time by design. Show below swap offers since they're rarer. */}
      {offer && swapOffers.length > 0 && <div class="section-header">Reemplazos por cancelación</div>}
      {!offer ? null : (
        <div class="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div class="lt-title" style={{ fontSize: '1.05rem' }}>{offer.title}</div>
            <div class="lt-sub">
              {offer.rol}
              {offer.startTime && ` · ${offer.startTime}`}
              {offer.date && ` · ${fmtFullDate(offer.date)}`}
            </div>
            {offer.location && (
              <div class="lt-sub">
                📍 {offer.mapsUrl
                  ? <a href={offer.mapsUrl} target="_blank" rel="noopener noreferrer">{offer.location}</a>
                  : offer.location}
              </div>
            )}
            {offer.expiresAt && (
              <div class="lt-sub" style={{ marginTop: 6, color: 'var(--color-warning)' }}>
                ⏳ {fmtExpiry(offer.expiresAt)}
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 16 }}>
            <button
              class="btn primary"
              disabled={submitting !== null}
              onClick={() => handleDecision('accept')}
            >
              {submitting === 'accept' ? 'Enviando...' : 'Aceptar'}
            </button>
            <button
              class="btn"
              disabled={submitting !== null}
              onClick={() => handleDecision('reject')}
            >
              {submitting === 'reject' ? 'Enviando...' : 'Rechazar'}
            </button>
          </div>
          <p class="hint" style={{ marginTop: 12, textAlign: 'center' }}>
            Tu respuesta también queda confirmada por DM con Sophi.
          </p>
        </div>
      )}
    </div>
  );
}
